/* Shared dark-mode-aware react-select styles (O-3508 / O-3554).

   react-select emits emotion-generated class names that CSS can't target, so the
   only reliable way to theme it is through its `styles` prop. These styles read
   the Setlist --sl-* design tokens with light fallbacks, so they resolve dark on
   a themed dark page (body.od-themed) and stay identical everywhere else — no
   per-page branching, no theme-name checks. --sl-* dark values cascade from
   body.od-themed, so a menu portaled to document.body still themes correctly.

   Usage:
     import darkSelectStyles, { mergeSelectStyles } from '../../shared/darkSelectStyles';
     <Select styles={darkSelectStyles} ... />
     // or, to add/override per-key style fns:
     <Select styles={mergeSelectStyles({ groupHeading: (b) => ({ ...b, ... }) })} ... />
*/

const darkSelectStyles = {
  container: (base, state) => ({
    ...base,
    opacity: state.isDisabled ? '.5' : '1',
    backgroundColor: 'transparent',
  }),
  control: (base) => ({
    ...base,
    borderRadius: '8px',
    border: '1px solid var(--sl-border-primary, #d5d7da)',
    backgroundColor: 'var(--sl-bg-primary, #fff)',
    boxShadow: 'none',
  }),
  menuPortal: (base) => ({ ...base, zIndex: 9999 }),
  menu: (base) => ({
    ...base,
    zIndex: '9999 !important',
    backgroundColor: 'var(--sl-bg-primary, #fff)',
    border: '1px solid var(--sl-border-primary, #d5d7da)',
  }),
  singleValue: (base) => ({ ...base, color: 'var(--sl-text-primary, #181d27)' }),
  multiValue: (base) => ({ ...base, backgroundColor: 'var(--sl-bg-secondary-hover, #f5f5f5)' }),
  multiValueLabel: (base) => ({ ...base, color: 'var(--sl-text-primary, #181d27)' }),
  input: (base) => ({ ...base, color: 'var(--sl-text-primary, #181d27)' }),
  placeholder: (base) => ({ ...base, color: 'var(--sl-text-placeholder, #717680)' }),
  option: (base, state) => ({
    ...base,
    backgroundColor: state.isFocused ? 'var(--sl-bg-secondary-hover, #f5f5f5)' : 'var(--sl-bg-primary, #fff)',
    color: 'var(--sl-text-primary, #181d27)',
  }),
  groupHeading: (base) => ({
    ...base,
    fontSize: '12px',
    fontWeight: 'normal',
    color: 'var(--sl-text-secondary, #333)',
    paddingLeft: '9px',
    paddingTop: '8px',
    paddingBottom: '4px',
    marginTop: '0px',
    marginBottom: '0px',
    textTransform: 'none',
  }),
  group: (base) => ({ ...base, paddingTop: '0px', paddingBottom: '0px' }),
};

/* Merge per-key overrides on top of the shared styles. Each override receives
   the shared style's already-computed result as its base, so callers can extend
   rather than replace. */
export const mergeSelectStyles = (overrides = {}) => {
  const merged = { ...darkSelectStyles };
  Object.keys(overrides).forEach((key) => {
    const baseFn = darkSelectStyles[key];
    const overrideFn = overrides[key];
    merged[key] = (base, state) => overrideFn(baseFn ? baseFn(base, state) : base, state);
  });
  return merged;
};

export default darkSelectStyles;
